/**
 * entity_list block. Only the newest list in the thread is interactive.
 * Markup and class names follow the web assistant EntityList.
 */

import { crmNavigation } from '../../../../services/crm-navigation.js';
import { t } from '../../../../i18n.js';
import { Icons } from '../../utils/icons.js';
import {
  canShowMore,
  isNewestEntityList,
  isRowPinned,
  listPageSize,
  listWindowBounds,
  rowSecondaryLine,
} from '../../list-selection.js';

const ENTITY_TYPE_KEYS = {
  activity: 'ai_entity_activity',
  contact: 'ai_entity_contact',
  lead: 'ai_entity_lead',
  customer: 'ai_entity_customer',
  campaign: 'ai_entity_campaign',
  user: 'ai_entity_user',
};

function entityTypeName(entityType) {
  const key = ENTITY_TYPE_KEYS[entityType];
  return key ? t(key) : entityType || '';
}

function textButton(label, { disabled = false, onClick }) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'ai-elist__btn';
  button.textContent = label;
  button.disabled = disabled;
  if (!disabled && onClick) button.addEventListener('click', onClick);
  return button;
}

/**
 * @param {Object} block
 * @param {Object} [options]
 * @param {string} [options.messageId]
 * @param {number} [options.partIndex]
 * @param {Object} [options.listSelection]
 * @returns {HTMLElement}
 */
export function createEntityList(block, options = {}) {
  const selection = options.listSelection || {};
  const interactive = isNewestEntityList(selection.newest, options.messageId, options.partIndex);
  const streaming = !!selection.streaming;
  const locked = !interactive || streaming;
  const pin = selection.pin || null;
  const exhausted = selection.exhaustedListIds;
  const rows = Array.isArray(block?.rows) ? block.rows : [];
  const pageSize = listPageSize(block);
  const bounds = listWindowBounds(block);
  const entityLabel = entityTypeName(block?.entityType);

  const root = document.createElement('div');
  root.className = `ai-elist${interactive ? '' : ' ai-elist--stale'}`;

  const head = document.createElement('div');
  head.className = 'ai-elist__head';

  const top = document.createElement('div');
  top.className = 'ai-elist__top';

  const typeEl = document.createElement('span');
  typeEl.className = 'ai-elist__entity';
  typeEl.textContent = entityLabel;

  const windowEl = document.createElement('span');
  windowEl.className = 'ai-elist__count';
  windowEl.textContent =
    bounds.total == null
      ? t('ai_list_window_open', { from: bounds.from, to: bounds.to })
      : t('ai_list_window', { from: bounds.from, to: bounds.to, total: bounds.total });

  top.append(typeEl, windowEl);
  head.appendChild(top);

  const filters = Array.isArray(block?.filters) ? block.filters : [];
  if (filters.length) {
    const chips = document.createElement('div');
    chips.className = 'ai-elist__chips';
    filters.forEach((filter) => {
      if (!filter) return;
      const chip = document.createElement('span');
      chip.className = 'ai-elist__chip';
      const label = document.createElement('b');
      label.textContent = filter.label ?? '';
      chip.append(label, document.createTextNode(` ${filter.value ?? ''}`));
      chips.appendChild(chip);
    });
    if (chips.childNodes.length) head.appendChild(chips);
  }

  root.appendChild(head);

  const rowList = document.createElement('div');
  rowList.className = 'ai-elist__rows';
  rowList.setAttribute('role', 'listbox');
  rowList.setAttribute('aria-label', entityLabel);

  rows.forEach((row) => {
    const selected = interactive && isRowPinned(pin, block, row);
    const rowEl = document.createElement('div');
    rowEl.className = `ai-elist__row${selected ? ' ai-elist__row--sel' : ''}${locked ? ' ai-elist__row--off' : ''}`;
    rowEl.setAttribute('role', 'option');
    rowEl.setAttribute('aria-selected', selected ? 'true' : 'false');
    rowEl.setAttribute('aria-disabled', locked ? 'true' : 'false');
    rowEl.tabIndex = locked ? -1 : 0;

    const selectRow = () => {
      if (locked) return;
      if (selected) selection.onClearPin?.();
      else selection.onPinRow?.(block, row);
    };

    rowEl.addEventListener('click', selectRow);
    rowEl.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (event.target !== event.currentTarget) return;
      event.preventDefault();
      selectRow();
    });

    const indexEl = document.createElement('span');
    indexEl.className = 'ai-elist__num';
    indexEl.textContent = row?.index == null ? '' : String(row.index);

    const main = document.createElement('span');
    main.className = 'ai-elist__main';

    const title = document.createElement('span');
    title.className = 'ai-elist__title';
    title.textContent = row?.label ?? '';
    main.appendChild(title);

    const secondary = rowSecondaryLine(row);
    if (secondary) {
      const sub = document.createElement('span');
      sub.className = 'ai-elist__sub';
      sub.textContent = secondary;
      main.appendChild(sub);
    }

    rowEl.append(indexEl, main);

    const canOpen = block?.entityType !== 'user' && row?.entityId != null && row.entityId !== '';
    if (canOpen) {
      const openWrap = document.createElement('span');
      openWrap.className = 'ai-elist__open';
      openWrap.addEventListener('click', (event) => event.stopPropagation());

      const open = document.createElement('button');
      open.type = 'button';
      open.className = 'ai-elist__open-link';
      open.setAttribute('aria-label', t('ai_open_record'));
      open.innerHTML = Icons.exportOutlined();
      open.addEventListener('click', (event) => {
        event.stopPropagation();
        crmNavigation.openEntity(block.entityType, row.entityId);
      });
      openWrap.appendChild(open);
      rowEl.appendChild(openWrap);
    }

    rowList.appendChild(rowEl);
  });

  root.appendChild(rowList);

  if (interactive) {
    const foot = document.createElement('div');
    foot.className = 'ai-elist__foot';

    if (canShowMore(block, exhausted)) {
      foot.appendChild(
        textButton(t('ai_show_more', { n: pageSize }), {
          disabled: streaming,
          onClick: () => selection.onShowMore?.(block),
        })
      );
    }

    foot.appendChild(
      textButton(t('ai_summarize_these', { n: pageSize }), {
        disabled: streaming,
        onClick: () => selection.onSummarizeList?.(block),
      })
    );

    root.appendChild(foot);
  }

  return root;
}
